// src/components/FileUploadDialog.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import axios from "axios";
import { API_BASE } from "../services/api";
import type { RawAxiosRequestHeaders } from "axios";

interface ApiResponse<T = any> {
  timestamp: string;
  success: boolean;
  message: string;
  error: string;
  data: T;
}

interface FileRowLite {
  file_id: string;
  version_no: number;
  version_kind: string;
  original_name: string;
}

interface Props {
  open: boolean;
  folderId: string; // 업로드 대상 폴더
  onClose: () => void;
  onUploaded: () => void; // 업로드 후 리스트 새로고침
}

// ✅ 백엔드 지원 포맷 (file_pipeline.py 기준)
const SUPPORTED_EXTENSIONS = [
  ".pdf",
  ".doc", ".docx",
  ".ppt", ".pptx",
  ".xls", ".xlsx",
  ".hwp", ".hwpx",
  ".txt", ".csv"
];

// accept 속성용 문자열 (예: ".pdf,.doc,.docx,...")
const ACCEPT_STRING = SUPPORTED_EXTENSIONS.join(",");

// datetime-local 인풋용 현재 시각 포맷터 (YYYY-MM-DDTHH:mm)
const formatNowForDatetimeLocal = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");

  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hour = pad(d.getHours());
  const minute = pad(d.getMinutes());

  return `${year}-${month}-${day}T${hour}:${minute}`;
};

const FileUploadDialog: React.FC<Props> = ({
  open,
  folderId,
  onClose,
  onUploaded,
}) => {
  const [versionKind, setVersionKind] = useState<"full" | "patch">("full");
  const [baseFileId, setBaseFileId] = useState<string>("");
  // ✅ 기본값을 현재 시각으로 세팅
  const [effective, setEffective] = useState<string>(() =>
    formatNowForDatetimeLocal()
  ); // YYYY-MM-DDTHH:mm (local)
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [existing, setExisting] = useState<FileRowLite[]>([]);

  const getHeaders = (): RawAxiosRequestHeaders => ({
    "x-user-id": localStorage.getItem("user_id") || "",
    "x-access-token": localStorage.getItem("access_token") || "",
    "x-refresh-token": localStorage.getItem("refresh_token") || "",
  });

  // 다이얼로그 열릴 때마다 에러/선택값 일부 초기화
  useEffect(() => {
    if (open) {
      setError(null);
      // 효력 시작 기본값: 비어 있으면 현재 시각으로 세팅
      setEffective((prev) => prev || formatNowForDatetimeLocal());
      // 폴더만 바뀐 경우 이전 상태 유지할지 애매한데,
      // 일단 버전 종류/기준 버전/효력일은 그대로 두고 에러만 초기화.
      // 필요하면 여기서 setVersionKind("full") 등 초기화 가능.
    }
  }, [open]);

  // 폴더 내 기존 파일(버전) 목록 - base_file 선택용
  useEffect(() => {
    const fetchExisting = async () => {
      if (!open || !folderId) return;
      try {
        const res = await axios.get<ApiResponse<any[]>>(
          `${API_BASE}folders/${folderId}/files`,
          { headers: getHeaders() }
        );
        if (res.data?.success) {
          const rows: any[] = res.data.data || [];
          setExisting(
            rows.map((r) => ({
              file_id: r.file_id,
              version_no: r.version_no,
              version_kind: r.version_kind,
              original_name: r.original_name,
            }))
          );
        } else {
          setExisting([]);
        }
      } catch {
        setExisting([]);
      }
    };
    fetchExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, folderId]);

  const canSubmit = useMemo(() => {
    if (!file) return false;
    if (versionKind === "patch" && !baseFileId) return false;
    return true;
  }, [file, versionKind, baseFileId]);

  const toUtcIsoOrNull = (localDT: string) => {
    if (!localDT) return null;
    const d = new Date(localDT);
    if (isNaN(d.getTime())) return null;
    return d.toISOString(); // 백엔드가 UTC(ISO) 받는 전제
  };

  // ✅ 파일 선택 핸들러 (확장자 검증 추가)
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    if (e.target.files && e.target.files.length > 0) {
      const selected = e.target.files[0];
      const name = selected.name;
      const dotIndex = name.lastIndexOf(".");
      const ext = dotIndex !== -1 ? name.substring(dotIndex).toLowerCase() : "";

      if (!SUPPORTED_EXTENSIONS.includes(ext)) {
        setError(`지원되지 않는 파일 형식입니다. (${SUPPORTED_EXTENSIONS.join(", ")})`);
        e.target.value = ""; // 선택 취소
        setFile(null);
        return;
      }
      setFile(selected);
    }
  };

  const handleUpload = async () => {
    setError(null);
    if (!file) {
      setError("파일을 선택하세요.");
      return;
    }
    if (versionKind === "patch" && !baseFileId) {
      setError("기준 버전을 선택하세요.");
      return;
    }

    setBusy(true);
    try {
      const effIso = toUtcIsoOrNull(effective);

      // ✅ 새 백엔드 스펙: multipart/form-data + 실제 파일 업로드
      const form = new FormData();
      form.append("folder_id", folderId);
      form.append("version_kind", versionKind);
      if (versionKind === "patch") {
        form.append("base_file_id", baseFileId);
      }
      if (effIso) {
        form.append("effective_date", effIso);
      }
      form.append("file", file);

      const res = await axios.post<
        ApiResponse<{ file_id: string; version_no: number }>
      >(`${API_BASE}files`, form, {
        headers: {
          ...getHeaders(),
          // Content-Type은 브라우저가 boundary 포함해서 자동 설정하는 게 안전
        },
      });

      if (!res.data?.success) {
        setError(
          res.data?.error || res.data?.message || "업로드에 실패했습니다."
        );
        return;
      }

      // 상태 초기화
      setFile(null);
      setEffective(formatNowForDatetimeLocal()); // 업로드 후 다시 현재 시각으로 초기화
      setBaseFileId("");
      setVersionKind("full");

      onUploaded();
    } catch (e: any) {
      setError(e?.message || "요청에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) return; // 업로드 중에는 닫기 방지
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>새 버전 업로드</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <FormControl fullWidth>
            <InputLabel>버전 종류</InputLabel>
            <Select
              label="버전 종류"
              value={versionKind}
              onChange={(e) =>
                setVersionKind(e.target.value as "full" | "patch")
              }
            >
              <MenuItem value="full">FULL (전체본)</MenuItem>
              <MenuItem value="patch">PATCH (부분 수정본)</MenuItem>
            </Select>
          </FormControl>

          {versionKind === "patch" && (
            <FormControl fullWidth>
              <InputLabel>기준 버전 선택</InputLabel>
              <Select
                label="기준 버전 선택"
                value={baseFileId}
                onChange={(e) => setBaseFileId(e.target.value)}
              >
                {existing.map((v) => (
                  <MenuItem key={v.file_id} value={v.file_id}>
                    v{v.version_no} · {v.original_name} ({v.version_kind})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* 타임존 안내 문구 */}
          <Typography variant="caption" color="text.secondary">
            ※ 아래 시간은 현재 사용 중인 PC의 시간대(예: 한국 시간)를 기준으로 입력되며,
            서버에는 UTC+0 기준 시각으로 변환되어 저장됩니다.
          </Typography>

          <TextField
            label="효력 시작 (선택)"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={effective}
            onChange={(e) => setEffective(e.target.value)}
          />

          <Stack direction="row" alignItems="center" spacing={2}>
            <Button variant="outlined" component="label">
              파일 선택
              <input
                type="file"
                hidden
                onChange={handleFileChange}
                accept={ACCEPT_STRING} // ✅ 파일 포맷 제한
              />
            </Button>
            {file && (
              <Typography variant="body2" color="text.secondary">
                선택됨: {file.name} ({file.size.toLocaleString()} bytes)
              </Typography>
            )}
          </Stack>

          {!file && (
            <Typography variant="caption" color="text.secondary">
              지원 포맷: PDF, Office(Word, PPT, Excel, HWP), Text/CSV
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          취소
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!canSubmit || busy}
        >
          {busy ? "업로드 중..." : "업로드"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileUploadDialog;